import { useState } from 'react';
import { motion } from 'framer-motion';
import { CanvaSlideData, Theme } from '../slideTypes';
import { SettingsButton, SlideSettingsDrawer, DrawerSection, DrawerInput, DrawerTextarea, DrawerNote } from '../shared/SlideShared';

export function CanvaSlide({
  data,
  theme,
  isEditing,
  onUpdate,
}: {
  data: CanvaSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: CanvaSlideData) => void;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Helper to extract/validate Canva embed URL — handles plain URLs and HTML embed snippets
  const getCanvaEmbedUrl = (input: string): string | null => {
    if (!input) return null;
    // If input contains an iframe, extract the src attribute
    if (input.includes('<iframe')) {
      const srcMatch = input.match(/src=["']([^"']*canva\.com[^"']*)["']/);
      if (srcMatch) return srcMatch[1];
    }
    // Plain URL with /view path
    if (input.includes('canva.com/design/') && input.includes('/view')) return input.trim();
    // Try to find a Canva design ID anywhere in the input
    const designIdMatch = input.match(/canva\.com\/design\/([A-Za-z0-9_-]+)/);
    if (designIdMatch) return `https://www.canva.com/design/${designIdMatch[1]}/view?embed`;
    return null;
  };

  const embedUrl = getCanvaEmbedUrl(data.embedUrl);

  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title="Canva Settings"
    >
      <DrawerSection label="Canva embed URL or HTML">
        <DrawerTextarea
          value={data.embedUrl}
          onChange={(e) => onUpdate({ ...data, embedUrl: e.target.value })}
          placeholder="Paste a Canva URL or the full HTML embed code"
          rows={3}
        />
        {embedUrl && (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--pos, #047857)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            Valid Canva URL
          </p>
        )}
      </DrawerSection>
      <DrawerSection label="Title (accessibility)">
        <DrawerInput
          type="text"
          value={data.title || ''}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Descriptive title..."
        />
      </DrawerSection>
      <DrawerNote title="How to embed a Canva design">
        <ol style={{ listStyle: 'decimal', paddingLeft: 16, margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <li>Open your presentation in Canva</li>
          <li>Click "Share" → "More" → "Embed"</li>
          <li>Paste either the <strong>embed link</strong> or the full <strong>HTML embed code</strong></li>
        </ol>
      </DrawerNote>
    </SlideSettingsDrawer>
  );

  // Empty state
  if (!data.embedUrl) {
    return (
      <div className="relative h-full group" style={{ backgroundColor: theme.colors.surface }}>
        <div className="h-full flex flex-col items-center justify-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent, #3b82f6) 10%, transparent)' }}
          >
            <svg className="w-8 h-8" style={{ color: 'var(--accent, #3b82f6)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
          </div>
          <div className="text-center">
            <p className="font-medium text-sm mb-0.5" style={{ color: theme.colors.text }}>Canva Presentation</p>
            <p className="text-xs" style={{ color: theme.colors.textMuted }}>No embed URL configured</p>
          </div>
          {isEditing && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--accent, #2563eb)' }}
            >
              Configure URL
            </button>
          )}
        </div>
        {settingsDrawer}
      </div>
    );
  }

  // WYSIWYG: show live iframe + settings gear on hover
  return (
    <div className="relative h-full w-full group" style={{ backgroundColor: theme.colors.background }}>
      {!isEditing ? (
        <motion.div
          className="w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <iframe
            src={embedUrl || data.embedUrl}
            title={data.title || 'Canva presentation'}
            className="w-full h-full border-0"
            allowFullScreen
            allow="fullscreen"
            loading="lazy"
          />
        </motion.div>
      ) : (
        <iframe
          src={embedUrl || data.embedUrl}
          title={data.title || 'Canva presentation'}
          className="w-full h-full border-0"
          allowFullScreen
          allow="fullscreen"
          loading="lazy"
        />
      )}
      {isEditing && <SettingsButton onClick={() => setSettingsOpen(true)} />}
      {settingsDrawer}
    </div>
  );
}
